import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserX, Search, RotateCcw, ArrowLeft } from "lucide-react";

const API = "/api";

export default function InactiveStudents() {
  const { getAuthHeader } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [reactivating, setReactivating] = useState(null);

  const fetchInactive = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/students/inactive?limit=500`, { headers: getAuthHeader() });
      const data = response.data;
      setStudents(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error("Failed to fetch inactive students:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchInactive();
  }, [fetchInactive]);

  const handleReactivate = async (studentId, name) => {
    setReactivating(studentId);
    try {
      await axios.put(`${API}/students/${studentId}/status`, { status: "active" }, { headers: getAuthHeader() });
      toast.success(`${name} has been reactivated`);
      fetchInactive();
    } catch (error) {
      toast.error("Failed to reactivate student");
    } finally {
      setReactivating(null);
    }
  };

  const filtered = students.filter(s =>
    !searchQuery.trim() || s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="inactive-students-page">
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Dashboard
      </Link>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-slate-100">
            <UserX className="w-6 h-6 text-slate-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inactive Students</h1>
            <p className="text-sm text-slate-500">{students.length} student{students.length !== 1 ? "s" : ""} need follow-up</p>
          </div>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold">All Inactive Students</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search student name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-inactive-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <UserX className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">{searchQuery ? `No students match "${searchQuery}"` : "No inactive students"}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Student Name</TableHead>
                  <TableHead className="font-semibold text-center">Remaining Credits</TableHead>
                  <TableHead className="font-semibold text-center">Last Class Date</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((student) => (
                  <TableRow key={student.id} data-testid={`inactive-student-${student.id}`}>
                    <TableCell>
                      <Link to={`/students/${student.id}`} className="flex items-center gap-3 hover:opacity-80">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                          <span className="text-slate-600 font-semibold text-xs">
                            {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <span className="font-medium text-slate-900">{student.name}</span>
                      </Link>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        {student.remaining_classes}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-slate-600">
                      {student.last_class_date || "No classes"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        Inactive
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(student.id, student.name)}
                        disabled={reactivating === student.id}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`reactivate-btn-${student.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        {reactivating === student.id ? "Reactivating..." : "Reactivate"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
